import {
  useGeneralSettingsFormData,
  useUpdateGeneralSettings,
} from "@/api/cms.ts";
import CmsMaintenanceImageUpload from "@/components/cms/CmsMaintenanceImageUpload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import type { SupportedLocale } from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import type { GeneralSettings } from "@/lib/cms/cms-types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

const SETTINGS_KEY = ["cms", "general-settings"] as const;

interface LocaleContent {
  title: string;
  line1: string;
  line2: string;
}
type MaintenanceContentState = Record<string, LocaleContent>;

const LANGUAGE_LABELS: Record<string, string> = {
  fr: "Français",
  en: "English",
  es: "Español",
  nl: "Nederlands",
};

function emptyLocaleContent(): LocaleContent {
  return { title: "", line1: "", line2: "" };
}

function buildContentState(
  raw: GeneralSettings["maintenanceContent"] | undefined,
  languages: SupportedLocale[],
): MaintenanceContentState {
  const state: MaintenanceContentState = {};
  for (const lang of languages) {
    const entry = raw?.[lang];
    state[lang] = {
      title: entry?.title ?? "",
      line1: entry?.line1 ?? "",
      line2: entry?.line2 ?? "",
    };
  }
  return state;
}

/**
 * Page de maintenance de l'ancien CMS (`/cms/maintenance`).
 * Page simple : titre + 2 lignes par langue + 1 image, le tout centré côté site.
 * Réutilise les mêmes réglages `CMS.maintenance*` que le CMS avancé (mode SIMPLE).
 */
const CMSMaintenance = () => {
  const cmsNew = useCmsNew();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { instanceData } = useAuth();
  // Uniquement les langues actives du site.
  const { websiteLanguages, defaultWebsiteLanguage } = useCmsWebsiteLanguages();

  const { data: generalSettings } = useGeneralSettingsFormData();
  const updateSettings = useUpdateGeneralSettings();

  const maintenanceEnabled = generalSettings?.maintenanceEnabled ?? false;
  const cmsId = generalSettings?.id ?? instanceData?.CMS?.id;

  const [content, setContent] = useState<MaintenanceContentState>({});
  // Ne resynchronise l'état local que si le contenu SERVEUR change réellement,
  // pour ne pas écraser la saisie en cours lors des refetch déclenchés par
  // l'upload d'image ou la (dé)publication.
  const lastSyncedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!generalSettings) return;
    const serverContent = buildContentState(
      generalSettings.maintenanceContent,
      websiteLanguages,
    );
    const serialized = JSON.stringify(serverContent);
    if (serialized !== lastSyncedRef.current) {
      lastSyncedRef.current = serialized;
      setContent(serverContent);
    }
  }, [generalSettings, websiteLanguages]);

  const hasSimpleContent = useMemo(() => {
    const primary = content[defaultWebsiteLanguage];
    return Boolean(primary?.title?.trim());
  }, [content, defaultWebsiteLanguage]);

  const saveSettings = (patch: {
    maintenanceEnabled?: boolean;
    maintenanceContent?: MaintenanceContentState;
  }) => {
    if (!cmsId) {
      toast({
        title: cmsNew(
          "cms.new.auto.title.impossible_denregistrer_cms_introuvable",
          "Impossible d'enregistrer : CMS introuvable",
        ),
        variant: "error",
      });
      return;
    }
    updateSettings.mutate(
      {
        id: cmsId,
        headerAlwaysVisible: generalSettings?.headerAlwaysVisible ?? false,
        headerAlwaysVisibleMobile:
          generalSettings?.headerAlwaysVisibleMobile ?? false,
        socials: generalSettings?.socials ?? [],
        isMixedCRMode: generalSettings?.isMixedCRMode ?? false,
        defaultWebsiteLanguage: generalSettings?.defaultWebsiteLanguage ?? "fr",
        websiteLanguages: generalSettings?.websiteLanguages ?? ["fr"],
        playerAutoplay: generalSettings?.playerAutoplay ?? false,
        playerControls: generalSettings?.playerControls ?? true,
        playerMuted: generalSettings?.playerMuted ?? false,
        playerLoop: generalSettings?.playerLoop ?? false,
        maintenanceEnabled,
        // L'ancien CMS ne gère que la page simple.
        maintenanceMode: "SIMPLE",
        maintenancePageId: generalSettings?.maintenancePageId ?? null,
        abonnementPageId: generalSettings?.abonnementPageId ?? null,
        ...patch,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
          toast({
            title: cmsNew(
              "cms.new.auto.title.reglages_maintenance_enregistres",
              "Réglages maintenance enregistrés",
            ),
            variant: "success",
            duration: 1600,
          });
        },
        onError: () => {
          toast({
            title: cmsNew(
              "cms.new.auto.title.impossible_denregistrer",
              "Impossible d'enregistrer",
            ),
            variant: "error",
          });
        },
      },
    );
  };

  const togglePublish = (next: boolean) => {
    if (next && !hasSimpleContent) {
      toast({
        title: cmsNew(
          "cms.new.maintenance.publishNeedsTitle",
          "Renseignez au moins un titre dans la langue principale avant de publier.",
        ),
        variant: "error",
      });
      return;
    }
    saveSettings({ maintenanceEnabled: next });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    saveSettings({ maintenanceContent: content });
  };

  const updateLocaleField = (
    lang: string,
    field: keyof LocaleContent,
    value: string,
  ) => {
    setContent((prev) => ({
      ...prev,
      [lang]: { ...(prev[lang] ?? emptyLocaleContent()), [field]: value },
    }));
  };

  const languageLabel = (lang: string) =>
    LANGUAGE_LABELS[lang] ?? lang.toUpperCase();

  return (
    <CMSLayout
      pageName={cmsNew("cms.new.auto.title.maintenance", "Maintenance")}
      pageSubtitle={cmsNew(
        "cms.new.maintenance.subtitleSimple",
        "Affichez une page de maintenance à tous les visiteurs (titre, 2 lignes de texte et une image, centrés).",
      )}
      formIds={["maintenance-simple"]}
      havePublishedSwitch
      isPublished={maintenanceEnabled}
      setIsPublished={togglePublish}
      hidePageAppearanceButton
    >
      <div className="flex w-full flex-col gap-6">
        <p className="text-sm text-slate-600">
          {cmsNew(
            "cms.new.maintenance.redirectHintSimple",
            "Lorsque la maintenance est publiée, le site public redirige tous les visiteurs vers la page de maintenance. Le bouton « Voir le site » (en haut) et les sessions backoffice continuent d'accéder au site normalement.",
          )}
        </p>

        {!hasSimpleContent ? (
          <p className="text-sm text-amber-700">
            {cmsNew(
              "cms.new.maintenance.noSimpleContentHint",
              "Renseignez au moins un titre dans la langue principale pour pouvoir publier la page.",
            )}
          </p>
        ) : null}

        {/* Image (partagée par toutes les langues) */}
        <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
          <Label className="text-sm font-medium text-slate-800">
            {cmsNew("cms.new.maintenance.imageLabel", "Image")}
          </Label>
          <p className="text-xs text-slate-500">
            {cmsNew(
              "cms.new.maintenance.imageHint",
              "Une seule image, partagée par toutes les langues, affichée centrée.",
            )}
          </p>
          <CmsMaintenanceImageUpload />
        </div>

        {/* Textes par langue */}
        <form
          id="maintenance-simple"
          onSubmit={handleSubmit}
          className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
        >
          <Label className="text-sm font-medium text-slate-800">
            {cmsNew("cms.new.maintenance.textsLabel", "Textes par langue")}
          </Label>

          <Tabs defaultValue={defaultWebsiteLanguage}>
            <TabsList>
              {websiteLanguages.map((lang) => (
                <TabsTrigger key={lang} value={lang}>
                  {languageLabel(lang)}
                </TabsTrigger>
              ))}
            </TabsList>

            {websiteLanguages.map((lang) => {
              const entry = content[lang] ?? emptyLocaleContent();
              return (
                <TabsContent key={lang} value={lang} className="space-y-3 pt-3">
                  <div className="space-y-1">
                    <Label htmlFor={`m-title-${lang}`} className="text-xs text-slate-600">
                      {cmsNew("cms.new.maintenance.fieldTitle", "Titre")}
                    </Label>
                    <Input
                      id={`m-title-${lang}`}
                      value={entry.title}
                      onChange={(e) =>
                        updateLocaleField(lang, "title", e.target.value)
                      }
                      placeholder={cmsNew(
                        "cms.new.maintenance.fieldTitlePlaceholder",
                        "Site en maintenance",
                      )}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`m-line1-${lang}`} className="text-xs text-slate-600">
                      {cmsNew("cms.new.maintenance.fieldLine1", "Ligne de texte 1")}
                    </Label>
                    <Textarea
                      id={`m-line1-${lang}`}
                      value={entry.line1}
                      rows={2}
                      onChange={(e) =>
                        updateLocaleField(lang, "line1", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`m-line2-${lang}`} className="text-xs text-slate-600">
                      {cmsNew("cms.new.maintenance.fieldLine2", "Ligne de texte 2")}
                    </Label>
                    <Textarea
                      id={`m-line2-${lang}`}
                      value={entry.line2}
                      rows={2}
                      onChange={(e) =>
                        updateLocaleField(lang, "line2", e.target.value)
                      }
                    />
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </form>
      </div>
    </CMSLayout>
  );
};

export default CMSMaintenance;
